import { differenceInYears } from 'date-fns';
import { getAllowedDonorAge } from 'modules/donor/utils/get-allowed-donor-age';
import React, { useMemo, FC } from 'react';
import { Control, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { getPetFields } from '../fields';
import FormSection from '../FormSection';

export const PetFormItem: FC<{
  index: number;
  control: Control<any>;
  isFirstPet: boolean;
  sharedRegion?: string;
  sharedLocation?: string;
  isDaycareLocationSelected: boolean;
  microchipPriceText: string;
}> = React.memo(
  ({
    index,
    control,
    isFirstPet,
    sharedLocation,
    sharedRegion,
    isDaycareLocationSelected,
    microchipPriceText,
  }) => {
    const { t } = useTranslation('donor.RegisterPetForm');

    const petSpecies = useWatch({
      control,
      name: `pets.${index}.species`,
    });
    const hasMicrochip = useWatch({
      control,
      name: `pets.${index}.hasMicrochip`,
    });
    const dateOfBirth = useWatch({
      control,
      name: `pets.${index}.dateOfBirth`,
    });

    const donorAgeRange = getAllowedDonorAge(petSpecies);
    const currentAge = differenceInYears(new Date(), dateOfBirth);
    const showAgeWarning =
      Boolean(dateOfBirth) &&
      (currentAge < donorAgeRange.min || currentAge >= donorAgeRange.max);

    const fields = useMemo(
      () =>
        getPetFields({
          donorSpecie: petSpecies,
          sharedRegion,
          isFirstPet,
          sharedLocation,
        }),
      [petSpecies, sharedRegion, isFirstPet, sharedLocation]
    );

    const showMicrochipAlert = hasMicrochip === false;

    return (
      <FormSection
        fields={fields}
        fieldNamePrefix={`pets.${index}`}
        isDaycareLocationSelected={isDaycareLocationSelected}
        showMicrochipAlert={showMicrochipAlert}
        microchipAlertMessage={t('microchipRequirementMessage', {
          price: microchipPriceText,
        })}
        showAgeWarning={showAgeWarning}
        ageWarningMessage={t('donorAgeIsOutOfRangeWarning', donorAgeRange)}
      />
    );
  }
);
